import { getVulnerabilitiesByResearcherId } from '@/services';
import type { IQueryParams, Vulnerability } from '@/types';
import { calculatePageResults, PAGE_LIMIT } from '@/utils';
import { defineStore } from 'pinia';

type TabKey = 'all' | 'in-review' | 'validated' | 'published' | 'rejected';

type State = {
  [key in TabKey]: {
    loading: boolean;
    loaded: boolean;
    data: Record<string, Vulnerability[]>;
    hasMore: boolean;
    batchInfo: string;
    page: number;
  };
};

export const useVulnerabilitiesStore = defineStore('vulnerabilities', {
  state: (): State => ({
    all: {
      loading: false,
      loaded: false,
      data: {},
      hasMore: false,
      batchInfo: '',
      page: 1
    },
    'in-review': {
      loading: false,
      loaded: false,
      data: {},
      hasMore: false,
      batchInfo: '',
      page: 1
    },
    validated: {
      loading: false,
      loaded: false,
      data: {},
      hasMore: false,
      batchInfo: '',
      page: 1
    },
    published: {
      loading: false,
      loaded: false,
      data: {},
      hasMore: false,
      batchInfo: '',
      page: 1
    },
    rejected: {
      loading: false,
      loaded: false,
      data: {},
      hasMore: false,
      batchInfo: '',
      page: 1
    }
  }),
  actions: {
    async fetchTabData(id: string, tab: TabKey, query: IQueryParams) {
      try {
        if (this.$state[tab].loaded) return;

        this.$state[tab].loading = true;
        const { data } = await getVulnerabilitiesByResearcherId(id, tab, query);
        if (data.value) {
          const { from, to } = calculatePageResults(
            this.$state[tab].page,
            data.value?.pagination.total,
            PAGE_LIMIT
          );
          this.$state[tab].data[this.$state[tab].page] = data.value.data;
          this.$state[tab].hasMore =
            data.value?.pagination.next_page_url !== null;
          this.$state[tab].loaded = true;
          this.$state[tab].batchInfo = `Results ${from} - ${to}`;
        }
      } catch (err) {
        throw new Error('Error fetching tab data');
      } finally {
        this.$state[tab].loading = false;
      }
    },
    $reset(tab: TabKey) {
      this.$state[tab].loaded = false;
    },
    resetData(tab: TabKey) {
      this.$state[tab].data = {};
      this.$state[tab].page = 1;
    },
    loadMore(tab: TabKey) {
      this.$state[tab].page++;
      this.$reset(tab);
    }
  }
});
